import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronLeft, 
  ChevronRight,
  Users,
  Shield,
  Award,
  Recycle,
  Flame,
  Phone
} from 'lucide-react';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Your Waste is Our Responsibility",
      subtitle: "Leading Biohazard & Hazardous Waste Management",
      description: "Transforming waste into valuable resources while building safer, cleaner societies across Uganda",
      image: "/garbagetruck.jpg",
      primaryCTA: { text: "Partner With Us", link: "/contact" },
      secondaryCTA: { text: "Emergency: +256 393 254458", link: "tel:+256393254458" },
      stats: [
        { icon: <Users className="w-5 h-5" />, number: "200+", label: "Clients Served" },
        { icon: <Shield className="w-5 h-5" />, number: "99.9%", label: "Safety Record" }
      ]
    },
    {
      id: 2,
      title: "Medical Waste Disposal Excellence",
      subtitle: "Safe & Compliant Healthcare Solutions",
      description: "Comprehensive medical waste management with full regulatory compliance and 24/7 emergency response",
      image: "/companyassets.jpg",
      primaryCTA: { text: "Our Services", link: "/services" },
      secondaryCTA: { text: "Get Quote", link: "/contact" },
      stats: [
        { icon: <Shield className="w-5 h-5" />, number: "24/7", label: "Emergency Response" },
        { icon: <Award className="w-5 h-5" />, number: "100%", label: "Compliance" }
      ]
    },
    {
      id: 3,
      title: "Sustainable Waste Solutions",
      subtitle: "Building a Circular Economy",
      description: "85% waste diversion from landfills through innovative recycling and resource recovery processes",
      image: "/companyoffice.jpg",
      primaryCTA: { text: "Sustainability", link: "/sustainability" },
      secondaryCTA: { text: "Learn More", link: "/about" },
      stats: [
        { icon: <Recycle className="w-5 h-5" />, number: "85%", label: "Waste Diverted" },
        { icon: <Flame className="w-5 h-5" />, number: "60%", label: "Emission Reduction" }
      ]
    },
    {
      id: 4,
      title: "Trusted by Leading Organizations",
      subtitle: "Ministry of Health • Mulago Hospital • Makerere University",
      description: "Strategic partnerships with Uganda's most prestigious institutions drive our commitment to excellence",
      image: "/work.jpg",
      primaryCTA: { text: "Our Clients", link: "/clientele" },
      secondaryCTA: { text: "Join Network", link: "/contact" },
      stats: [
        { icon: <Users className="w-5 h-5" />, number: "150+", label: "Healthcare Partners" },
        { icon: <Award className="w-5 h-5" />, number: "4.9/5", label: "Client Satisfaction" }
      ]
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative h-screen flex items-start justify-center overflow-hidden w-full max-w-full">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img 
            src={slide.image} 
            alt={slide.title}
            className="w-full h-full object-cover absolute top-0 left-0 right-0"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#295cab]/60 to-[#1e4a8a]/60"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center text-white w-full pt-16">
        <div className="max-w-4xl mx-auto">
          {/* Main Content */}
          <div className="mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight animate-fade-in break-words">
              {currentSlideData.title}
            </h1>
            <h2 className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-semibold mb-4 sm:mb-6 text-blue-200 animate-fade-in-delay-1">
              {currentSlideData.subtitle}
            </h2>
            <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl mb-8 sm:mb-10 text-blue-100 max-w-3xl mx-auto leading-relaxed animate-fade-in-delay-2 break-words">
              {currentSlideData.description}
            </p>
          </div>

          {/* Stats */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8 md:gap-12 mb-8 sm:mb-12 animate-fade-in-delay-3">
            {currentSlideData.stats.map((stat, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                <div className="text-blue-300">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold">{stat.number}</div>
                  <div className="text-sm text-blue-200">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center animate-fade-in-delay-4">
            <Link 
              to={currentSlideData.primaryCTA.link}
              className="bg-transparent border-2 border-[#295cab] text-[#295cab] px-8 py-4 rounded-lg font-semibold hover:bg-[#295cab]/20 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 text-lg backdrop-blur-sm"
            >
              <span>{currentSlideData.primaryCTA.text}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a 
              href={currentSlideData.secondaryCTA.link}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#295cab] transition-all flex items-center justify-center space-x-2 text-lg"
            >
              {currentSlideData.secondaryCTA.link.startsWith('tel:') && <Phone className="w-5 h-5" />}
              <span>{currentSlideData.secondaryCTA.text}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 sm:left-8 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all backdrop-blur-sm border border-white/20"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-4 sm:right-8 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all backdrop-blur-sm border border-white/20"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-white scale-125' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20 z-20">
        <div 
          className="h-full bg-white transition-all duration-300 ease-linear"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>
    </section>
  );
};

export default HeroSlider;